if (!customElements.get('product-opt')) {
  customElements.define(
    'product-opt',
    class ProductOpt extends window.BAO.CustomElement {
      constructor() {
        super()

        this.colourEls = this.querySelectorAll('[data-el="product-colours.colour"]')

        if (!this.colourEls.length) return

        this.initialiseColourLinks()
      }

      initialiseColourLinks() {
        this.colourEls.forEach(colourEl => {
          if (colourEl.getAttribute('aria-current') === 'true') return

          colourEl.addEventListener('click', this.handleColourClick)
        })
      }

      handleColourClick = event => {
        const colourEl = event.currentTarget
        const href = colourEl.getAttribute('href')
        const mainProduct = this.closest('main-product')

        if (!href || colourEl.tagName !== 'A' || !mainProduct) return

        event.preventDefault()
        if (colourEl.getAttribute('aria-current') === 'true') return

        this.saveSelectedSize()
        this.showSelectedColour(colourEl)
        this.renderProduct(href, mainProduct)
      }

      showSelectedColour(colourEl) {
        this.colourEls.forEach(el => {
          el.setAttribute('aria-current', el === colourEl ? 'true' : 'false')
        })

        const name = colourEl.dataset.productColoursName
        const colours = colourEl.closest('product-colours')
        if (!name || !colours) return

        colours.dataset.productColoursDefault = name
        const title = colours.querySelector('[data-el="product-colours.title"]')
        if (title) title.textContent = name
      }

      saveSelectedSize() {
        const selected = document.querySelector(
          '[data-el="product-options.trigger"][aria-pressed="true"]',
        )

        if (!selected || !selected.dataset.value) return

        sessionStorage.setItem('savedSize', selected.dataset.value)
      }

      renderProduct(url, mainProduct) {
        this.setAttribute('aria-busy', 'true')

        fetch(url)
          .then(response => {
            if (!response.ok) throw new Error('Product request failed')
            return response.text()
          })
          .then(responseText => {
            const html = new DOMParser().parseFromString(responseText, 'text/html')
            const nextContent = html.querySelector('#MainContent')
            const liveContent = document.querySelector('#MainContent')
            const nextProduct = nextContent?.querySelector('main-product')

            if (!nextContent || !liveContent || !nextProduct) {
              window.location.href = url
              return
            }

            const marker = html.createComment('product')
            nextProduct.replaceWith(marker)

            const title = html.querySelector('title')
            if (title) document.title = title.textContent

            const loadedScriptSrcs = new Set(
              [...document.scripts].map(script => script.getAttribute('src')).filter(Boolean),
            )

            const renderId = (liveContent._productRenderId || 0) + 1
            liveContent._productRenderId = renderId
            const trustpilotWidgets = this.keepTrustpilotWidgets(liveContent)

            mainProduct.replaceWith(nextProduct)
            this.restoreTrustpilotWidgets(liveContent, trustpilotWidgets)
            window.history.replaceState({}, '', url)
            window.scrollTo({ top: 0, behavior: 'smooth' })

            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (liveContent._productRenderId !== renderId) return
                if (!nextProduct.isConnected) return

                this.replacePageContent(liveContent, nextContent, marker, nextProduct)
                this.runInsertedScripts(liveContent, loadedScriptSrcs)
                this.replaceProductBackground(html)
                this.restoreTrustpilotWidgets(liveContent, trustpilotWidgets)
                liveContent.querySelectorAll(':scope > .shopify-section').forEach(section => {
                  const sectionId = section.id.replace(/^shopify-section-/, '')
                  section.dispatchEvent(new CustomEvent('shopify:section:load', {
                    bubbles: true,
                    detail: { sectionId },
                  }))
                })
              })
            })
          })
          .catch(() => {
            window.location.href = url
          })
      }

      keepTrustpilotWidgets(root) {
        return [...root.querySelectorAll('.trustpilot-widget')].map(widget => {
          widget.remove()
          return widget
        })
      }

      restoreTrustpilotWidgets(root, widgets) {
        widgets.forEach(widget => {
          const templateId = widget.getAttribute('data-template-id')
          const incoming = [...root.querySelectorAll('.trustpilot-widget')].find(el => {
            return el !== widget && el.getAttribute('data-template-id') === templateId
          })

          if (incoming) incoming.replaceWith(widget)
        })
      }

      replacePageContent(liveContent, nextContent, marker, liveProduct) {
        const liveSection = liveProduct.closest('#MainContent > *') || liveProduct
        const nextSections = [...nextContent.children]
        const nextProductSection = nextSections.find(section => section.contains(marker))
        const deferredNavigations = []

        nextSections.forEach(section => {
          if (section === nextProductSection) return

          section.querySelectorAll('product-navigation').forEach(navigation => {
            const placeholder = document.createComment('product-navigation')
            navigation.replaceWith(placeholder)
            deferredNavigations.push({ placeholder, navigation })
          })
        })

        const desired = nextSections.map(section => (
          section === nextProductSection ? liveSection : section
        ))

        desired.forEach((node, index) => {
          const current = liveContent.children[index]
          if (current === node) return
          if (current) liveContent.insertBefore(node, current)
          else liveContent.appendChild(node)
        })

        while (liveContent.children.length > desired.length) {
          const last = liveContent.lastElementChild
          if (last === liveSection) break
          last.remove()
        }

        if (nextProductSection && liveSection !== liveProduct) {
          const extras = [...nextProductSection.childNodes]
          const markerIndex = extras.indexOf(marker)

          ;[...liveSection.childNodes].forEach(node => {
            if (node === liveProduct || node.contains?.(liveProduct)) return
            node.remove()
          })

          extras.slice(0, markerIndex).forEach(node => liveProduct.before(node))
          extras.slice(markerIndex + 1).forEach(node => liveSection.appendChild(node))
        }

        deferredNavigations.forEach(({ placeholder, navigation }) => {
          if (placeholder.parentNode) placeholder.replaceWith(navigation)
        })
      }

      runInsertedScripts(root, loadedScriptSrcs) {
        const executable = script => {
          const type = (script.getAttribute('type') || '').toLowerCase()
          return type === '' || type === 'text/javascript' || type === 'application/javascript' || type === 'module'
        }

        ;[...root.querySelectorAll('script')].forEach(script => {
          if (!executable(script) || script.closest('main-product')) return

          const src = script.getAttribute('src')
          const type = (script.getAttribute('type') || '').toLowerCase()

          if (src && type === 'module' && loadedScriptSrcs.has(src)) return

          if (!src) {
            const inline = document.createElement('script')
            inline.text = `{\n${script.text}\n}`
            script.replaceWith(inline)
            return
          }

          const next = document.createElement('script')
          ;[...script.attributes].forEach(attr => {
            if (attr.name === 'src' || attr.name === 'defer' || attr.name === 'async') return
            next.setAttribute(attr.name, attr.value)
          })
          if (type !== 'module') next.async = false
          next.src = src
          script.replaceWith(next)
        })
      }

      replaceProductBackground(html) {
        const nextBackground = html.querySelector('product-background')
        const liveBackground = document.querySelector('product-background')

        if (liveBackground && nextBackground) {
          ;[...liveBackground.attributes].forEach(attr => {
            if (!nextBackground.hasAttribute(attr.name)) liveBackground.removeAttribute(attr.name)
          })
          ;[...nextBackground.attributes].forEach(attr => {
            liveBackground.setAttribute(attr.name, attr.value)
          })
          liveBackground.innerHTML = nextBackground.innerHTML
          return
        }

        if (liveBackground) {
          liveBackground.remove()
          return
        }

        if (!nextBackground) return

        const footer = document.querySelector('.lyt-Site_Footer')
        if (footer) footer.after(nextBackground)
      }
    },
  )
}
